/**
 * Unified customer activity: the business-facing view of domain, billing,
 * Growth and payment trails. The API is the security boundary; the route is
 * gated on audit:read (`SECTION_PERMISSIONS`), and an API 403 shows the same
 * page. Filters, the page (`before`) and the open entry live in the URL.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronDown, ExternalLink, History } from "lucide-react";
import { useSearchParams, Link } from "react-router";

import { AccessDenied, useOrganization } from "@/auth/organization";
import { useUser } from "@/auth/session";
import { shortName } from "@/components/team/roles";
import { Badge, Button, cx, EmptyState, ErrorState, Input, Select, Spinner } from "@/components/ui";
import { identity, portal } from "@/lib/api";
import { formatDateTime, timeAgo } from "@/lib/format";
import { ApiError } from "@/lib/http";
import type { ActivityEvent } from "@/lib/types";

const panel = "rounded-[14px] border border-border bg-surface";
const BILLING_SUBJECTS = new Set(["subscription", "invoice", "payment_attempt", "refund", "dispute"]);

export function ActivityPage() {
  const me = useUser();
  const { orgId, current } = useOrganization();
  const [params, setParams] = useSearchParams();
  const type = params.get("type") ?? "";
  const actor = params.get("person") ?? "";
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const before = params.get("before") ?? "";

  const members = useQuery({ queryKey: ["members", orgId], queryFn: () => identity.members(orgId) });
  const events = useQuery({
    queryKey: ["activity", orgId, type, actor, from, to, before],
    queryFn: () =>
      portal.activity(orgId, {
        type: type || undefined,
        actor_user_id: actor || undefined,
        from: from || undefined,
        to: to || undefined,
        page_token: before || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  if (events.error instanceof ApiError && events.error.status === 403) {
    return <AccessDenied />;
  }

  const people = members.data?.items ?? [];
  const byId = new Map(people.map((m) => [m.user.id, m.user]));
  const actorName = (event: ActivityEvent) => {
    if (event.actor_kind === "staff") return "CortexGrip staff";
    if (event.actor_kind === "system" || !event.actor_user_id) return "CortexGrip";
    if (event.actor_user_id === me.id) return "You";
    const user = byId.get(event.actor_user_id);
    if (user) return shortName(user.display_name, user.email);
    if (event.actor_email) return event.actor_email;
    // Without the member list (e.g. a custom role lacking members:read) a stranger can't be told from a member.
    return members.isSuccess ? "A former member" : "A team member";
  };
  /** A filter change starts again from the newest entries. */
  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("before");
    next.delete("open");
    setParams(next);
  };
  const goTo = (token: string | null) => {
    const next = new URLSearchParams(params);
    if (token) next.set("before", token);
    else next.delete("before");
    next.delete("open");
    setParams(next);
  };

  const rows = events.data?.items ?? [];
  const nextToken = events.data?.next_page_token ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start gap-3.5">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-border bg-brand-50 text-fg-muted">
          <History className="size-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <h1 className="text-[26px] font-bold text-fg">Activity</h1>
          <p className="mt-1 text-sm text-fg-subtle">
            A read-only record of domain, billing, payment and Growth changes in {current.organization.name},
            with the actor, transition and affected record. Access and team changes are in the{" "}
            <Link className="text-brand-700 underline underline-offset-2 hover:no-underline" to="/audit">
              audit log
            </Link>
            .
          </p>
        </div>
      </div>

      <section className={cx(panel, "px-5 py-4")}>
        <div className="flex flex-wrap items-end gap-2 pb-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-fg-muted" htmlFor="activity-type">
              Type
            </label>
            <Select
              id="activity-type"
              aria-label="Activity type"
              value={type}
              onChange={(event) => setFilter("type", event.target.value)}
              className="h-8 w-auto rounded-full py-1 text-[13px]"
            >
              <option value="">Everything</option>
              <option value="domain">Domains</option>
              <option value="billing">Billing</option>
              <option value="payment">Payments</option>
              <option value="growth">Growth Intelligence</option>
            </Select>
          </div>
          {!members.isError && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-fg-muted" htmlFor="activity-person">
                Person
              </label>
              <Select
                id="activity-person"
                aria-label="Activity person"
                value={actor}
                onChange={(event) => setFilter("person", event.target.value)}
                className="h-8 w-auto rounded-full py-1 text-[13px]"
              >
                <option value="">Anyone</option>
                {people.map((member) => (
                  <option key={member.user.id} value={member.user.id}>
                    {member.user.id === me.id
                      ? "You"
                      : shortName(member.user.display_name, member.user.email)}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-semibold text-fg-muted" htmlFor="activity-from">
              From
            </label>
            <Input
              id="activity-from"
              aria-label="Activity from date"
              type="date"
              value={from}
              max={to || undefined}
              onChange={(event) => setFilter("from", event.target.value)}
              className="h-8 w-auto py-1 text-[13px]"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-fg-muted" htmlFor="activity-to">
              To
            </label>
            <Input
              id="activity-to"
              aria-label="Activity to date"
              type="date"
              value={to}
              min={from || undefined}
              onChange={(event) => setFilter("to", event.target.value)}
              className="h-8 w-auto py-1 text-[13px]"
            />
          </div>
          {events.isFetching && <Spinner className="mb-2 size-4" />}
        </div>

        {events.isPending && (
          <div className="flex justify-center border-t border-border py-10">
            <Spinner className="size-5" />
          </div>
        )}
        {events.isError && <ErrorState error={events.error} onRetry={() => void events.refetch()} />}
        {events.isSuccess && rows.length === 0 && (
          <EmptyState
            title="No activity found"
            description="Changes matching these filters will appear here."
          />
        )}
        {rows.length > 0 && (
          <ul
            className={cx(
              "divide-y divide-border border-t border-border",
              events.isPlaceholderData && "opacity-60",
            )}
          >
            {rows.map((event) => (
              <ActivityRow key={event.id} event={event} actor={actorName(event)} />
            ))}
          </ul>
        )}

        {(before || nextToken) && (
          <nav aria-label="Activity pages" className="flex justify-center gap-2 pt-4">
            {before && (
              <Button variant="secondary" size="sm" className="rounded-full" onClick={() => goTo(null)}>
                Newest
              </Button>
            )}
            {nextToken && (
              <Button
                variant="secondary"
                size="sm"
                className="rounded-full"
                loading={events.isPlaceholderData}
                onClick={() => goTo(nextToken)}
              >
                Older
              </Button>
            )}
          </nav>
        )}
      </section>
    </div>
  );
}

function ActivityRow({ event, actor }: { event: ActivityEvent; actor: string }) {
  const [params, setParams] = useSearchParams();
  const open = params.get("open") === event.id;
  const href = subjectHref(event);
  const toggle = () => {
    const next = new URLSearchParams(params);
    if (open) next.delete("open");
    else next.set("open", event.id);
    setParams(next);
  };
  return (
    <li className="py-3">
      <Button
        unstyled
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-start gap-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-fg">{event.summary}</span>
            <Badge tone="neutral">{actor}</Badge>
          </span>
          <span className="mt-1 block text-xs text-fg-muted">
            {event.before && <span>{compactValues(event.before)} </span>}
            {event.before && event.after && <span aria-hidden>→ </span>}
            {event.after && <span>{compactValues(event.after)}</span>}
            {!event.before && !event.after && <span>{event.subject_type}</span>}
          </span>
        </span>
        <span className="shrink-0 text-xs text-fg-subtle" title={formatDateTime(event.occurred_at)}>
          {timeAgo(event.occurred_at)}
        </span>
        <ChevronDown
          aria-hidden
          className={cx("mt-0.5 size-4 shrink-0 text-fg-subtle transition-transform", open && "rotate-180")}
        />
      </Button>
      {open && (
        <div className="mt-3 rounded-xl bg-muted px-4 py-3 text-xs text-fg-muted">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="text-fg-subtle">When</dt>
            <dd>{formatDateTime(event.occurred_at)}</dd>
            <dt className="text-fg-subtle">Actor</dt>
            <dd>{actor}</dd>
            <dt className="text-fg-subtle">Event</dt>
            <dd className="font-mono">{event.event_type}</dd>
            <dt className="text-fg-subtle">Record</dt>
            <dd>
              {href ? (
                <Link className="inline-flex items-center gap-1 text-brand-700 hover:underline" to={href}>
                  {event.subject_type} <ExternalLink className="size-3" aria-hidden />
                </Link>
              ) : (
                event.subject_type
              )}
              <span className="ml-2 font-mono break-all">{event.subject_id}</span>
            </dd>
          </dl>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <JsonBlock title="Before" value={event.before} />
            <JsonBlock title="After" value={event.after} />
          </div>
          {Object.keys(event.metadata).length > 0 && <JsonBlock title="Details" value={event.metadata} />}
        </div>
      )}
    </li>
  );
}

function JsonBlock({ title, value }: { title: string; value: Record<string, unknown> | null }) {
  return (
    <div>
      <p className="mb-1 font-semibold text-fg-subtle">{title}</p>
      <pre className="overflow-x-auto rounded-lg bg-surface px-3 py-2 font-mono text-[11px] text-fg-muted">
        {value ? JSON.stringify(value, null, 2) : "—"}
      </pre>
    </div>
  );
}

function compactValues(values: Record<string, unknown>): string {
  return Object.entries(values)
    .map(([key, value]) => `${key.replaceAll("_", " ")}: ${String(value)}`)
    .join(", ");
}

function subjectHref(event: ActivityEvent): string | null {
  const id = encodeURIComponent(event.subject_id);
  if (event.subject_type === "domain") return `/domains/${id}`;
  if (event.subject_type === "site" || event.subject_type === "website") return `/websites/${id}`;
  if (event.subject_type === "insight") return `/growth-intelligence/insights/${id}`;
  if (BILLING_SUBJECTS.has(event.subject_type) || event.subject_type.startsWith("billing")) return "/billing";
  return null;
}
